import { useEffect, useState, useMemo } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  Clock,
  UserCheck,
  UserX,
  Coffee,
  CalendarOff,
  CalendarX2,
  Pencil,
  Plus,
  Users,
  FileDown,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useReportDownload } from '@/hooks/useReportDownload';
import { useDescribeWorkLocations } from '@/hooks/useDescribeWorkLocation';
import { useTimeclockAdminStore } from '@/store/useTimeclockAdminStore';
import { useOvertimeStore } from '@/store/useOvertimeStore';
import { useEmployeeStore } from '@/store/useEmployeeStore';
import { Card } from '@/components/Shared/Card';
import { Button } from '@/components/Shared/Button';
import { Badge } from '@/components/Shared/Badge';
import { Loading } from '@/components/Shared/Loading';
import { EmptyState } from '@/components/Shared/EmptyState';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeader,
  TableCell,
} from '@/components/Shared/Table';
import {
  FacilityFlowCoreModulesPlanningWorkflowsTimeClockDailyAttendanceStatus as AttendanceStatus,
  FacilityFlowCoreModulesPlanningEntitiesTimeClockDayStatus as DayStatus,
  FacilityFlowCoreModulesPlanningEntitiesTimeClockEventType as EventType,
} from '@/api/generated/models';
import type {
  FacilityFlowCoreModulesPlanningWorkflowsTimeClockDailyEmployeeStatusDto,
  FacilityFlowCoreModulesPlanningWorkflowsTimeClockTimeClockDayDto,
  FacilityFlowCoreModulesPlanningWorkflowsTimeClockTimeClockEventDto,
} from '@/api/generated/models';
import { formatUtcToLocalTime, isoDate, addIsoDays } from '@/utils/dateUtils';
import { formatTimeSpan } from '@/utils/timeclockTotals';
import { sortEventsByTime } from '@/store/useTimeclockStore';
import { StampTime } from '@/components/Shared/StampTime';
import { WorkTimeWithCredit } from './components/WorkTimeWithCredit';
import TimeClockAdminEditSlideOver from './components/TimeClockAdminEditSlideOver';
import { OvertimeAccountCard } from './components/OvertimeAccountCard';
import { OvertimeMonthsTable } from './components/OvertimeMonthsTable';
import { OvertimeSetupSlideOver } from './components/OvertimeSetupSlideOver';
import { formatSignedHours } from './components/overtimeFormat';

const MONTH_NAMES_DE = [
  'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember',
];

export default function TimeClockAdminPage() {
  const { t } = useTranslation('timeclock');
  const { t: tReports } = useTranslation('reports');
  // Monthly report (Zeitbestätigung) of the selected employee and month.
  const { downloading: reportDownloading, download: downloadReportFn } = useReportDownload();
  const {
    dailyStatus,
    dailyDate,
    dailyLoading,
    dailyError,
    selectedEmployeeId,
    selectedMonth,
    employeeHistory,
    historyLoading,
    historyError,
    fetchDailyStatus,
    setDailyDate,
    setSelectedEmployee,
    setSelectedMonth,
    fetchEmployeeHistory,
    fetchEmployeeDay,
  } = useTimeclockAdminStore();

  const { employees, fetchEmployees, fetchEmployeeById } = useEmployeeStore();
  // Home office permission of employees outside the (active-only) list, loaded on demand.
  const [fetchedHomeOffice, setFetchedHomeOffice] = useState<Record<string, boolean>>({});

  const [editEntry, setEditEntry] = useState<FacilityFlowCoreModulesPlanningWorkflowsTimeClockTimeClockDayDto | null>(null);
  const [editEmployeeId, setEditEmployeeId] = useState('');
  const [editEmployeeName, setEditEmployeeName] = useState('');
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [dayLoadingForId, setDayLoadingForId] = useState<string | null>(null);
  const [isSetupOpen, setIsSetupOpen] = useState(false);

  const { balancesByEmployee, fetchEmployeeBalance } = useOvertimeStore();
  const selectedBalance = selectedEmployeeId ? balancesByEmployee[selectedEmployeeId] : undefined;
  const selectedEmployeeName = useMemo(() => {
    const match = employees.find((e) => e.id === selectedEmployeeId);
    return match ? `${match.firstName} ${match.lastName}` : '';
  }, [employees, selectedEmployeeId]);
  // The administration may set home office only for employees allowed to work from home.
  const allowsHomeOffice = (employeeId?: string | null) =>
    employees.find((e) => e.id === employeeId)?.allowsHomeOffice ?? fetchedHomeOffice[employeeId ?? ''] ?? false;

  // why: corrections also reach employees missing from the list (deactivated ones) — their
  // permission is loaded by id instead of being read as "not allowed".
  useEffect(() => {
    if (!editEmployeeId || editEmployeeId in fetchedHomeOffice) return;
    if (employees.some((e) => e.id === editEmployeeId)) return;
    fetchEmployeeById(editEmployeeId).then(() => {
      const employee = useEmployeeStore.getState().currentEmployee;
      if (employee?.id !== editEmployeeId) return;
      setFetchedHomeOffice((prev) => ({ ...prev, [editEmployeeId]: employee.allowsHomeOffice ?? false }));
    });
  }, [editEmployeeId, employees, fetchedHomeOffice, fetchEmployeeById]);

  // why: das Konto ist mitarbeiterbezogen, nicht monatsbezogen — es wird beim Wechsel des
  // Mitarbeiters geladen, nicht bei jedem Monatssprung der Historie.
  useEffect(() => {
    if (selectedEmployeeId) {
      fetchEmployeeBalance(selectedEmployeeId);
    }
  }, [selectedEmployeeId, fetchEmployeeBalance]);

  useEffect(() => {
    fetchDailyStatus(dailyDate);
    fetchEmployees({ status: 'active', pageSize: 200 });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- initial load only; date changes reload through their own handler
  }, []);

  // Group employees by status
  const statusGroups = useMemo(() => {
    const emps = dailyStatus?.employees ?? [];
    const groups: Record<number, FacilityFlowCoreModulesPlanningWorkflowsTimeClockDailyEmployeeStatusDto[]> = {
      [AttendanceStatus.NUMBER_1]: [],
      [AttendanceStatus.NUMBER_4]: [],
      [AttendanceStatus.NUMBER_3]: [],
      [AttendanceStatus.NUMBER_5]: [],
      [AttendanceStatus.NUMBER_2]: [],
      [AttendanceStatus.NUMBER_6]: [],
    };
    for (const emp of emps) {
      const status = emp.status ?? AttendanceStatus.NUMBER_6;
      if (groups[status]) {
        groups[status].push(emp);
      }
    }
    return groups;
  }, [dailyStatus?.employees]);

  const handleDateChange = (offset: number) => {
    setDailyDate(addIsoDays(dailyDate, offset));
  };

  const handleDateInput = (value: string) => {
    if (value) setDailyDate(value);
  };

  const goToToday = () => {
    setDailyDate(isoDate(new Date()));
  };

  const handleMonthChange = (offset: number) => {
    let { year, month } = selectedMonth;
    month += offset;
    if (month > 12) { month = 1; year++; }
    if (month < 1) { month = 12; year--; }
    setSelectedMonth({ year, month });
  };

  const openEdit = (entry: FacilityFlowCoreModulesPlanningWorkflowsTimeClockTimeClockDayDto) => {
    setEditEntry(entry);
    setEditEmployeeId(entry.employeeId ?? '');
    setEditEmployeeName(entry.employeeName ?? '');
    setIsEditOpen(true);
  };

  const openEditFromDailyStatus = async (emp: FacilityFlowCoreModulesPlanningWorkflowsTimeClockDailyEmployeeStatusDto) => {
    if (!emp.employeeId) return;
    setDayLoadingForId(emp.employeeId);
    const dayEntry = await fetchEmployeeDay(emp.employeeId, dailyDate);
    setDayLoadingForId(null);
    setEditEntry(dayEntry ?? { date: dailyDate, employeeId: emp.employeeId, employeeName: emp.employeeName, events: [] });
    setEditEmployeeId(emp.employeeId);
    setEditEmployeeName(emp.employeeName ?? '');
    setIsEditOpen(true);
  };

  // "Tag nachtragen": a whole day for the selected employee, date picked in the dialog.
  const [isAddDayOpen, setIsAddDayOpen] = useState(false);

  const closeEdit = () => {
    setIsEditOpen(false);
    setEditEntry(null);
  };

  const formatDateDisplay = (dateStr: string) => {
    const [y, m, d] = dateStr.split('-');
    return `${d}.${m}.${y}`;
  };

  const isToday = dailyDate === isoDate(new Date());

  const STATUS_DEFS: Array<{
    key: AttendanceStatus;
    label: string;
    tone: 'emerald' | 'amber' | 'red' | 'sky' | 'slate' | 'slate-soft';
    icon: React.ReactNode;
    collapsed?: boolean;
  }> = [
    {
      key: AttendanceStatus.NUMBER_1,
      label: t('dailyStatus.clockedIn'),
      tone: 'emerald',
      icon: <UserCheck className="w-3.5 h-3.5" strokeWidth={2} />,
    },
    {
      key: AttendanceStatus.NUMBER_4,
      label: t('dailyStatus.onBreak'),
      tone: 'amber',
      icon: <Coffee className="w-3.5 h-3.5" strokeWidth={2} />,
    },
    {
      key: AttendanceStatus.NUMBER_3,
      label: t('dailyStatus.notClockedIn'),
      tone: 'red',
      icon: <UserX className="w-3.5 h-3.5" strokeWidth={2} />,
    },
    {
      key: AttendanceStatus.NUMBER_5,
      label: t('dailyStatus.absent'),
      tone: 'sky',
      icon: <CalendarOff className="w-3.5 h-3.5" strokeWidth={2} />,
    },
    {
      key: AttendanceStatus.NUMBER_2,
      label: t('dailyStatus.clockedOut'),
      tone: 'slate',
      icon: <Clock className="w-3.5 h-3.5" strokeWidth={2} />,
    },
    {
      key: AttendanceStatus.NUMBER_6,
      label: t('dailyStatus.notScheduled'),
      tone: 'slate-soft',
      icon: <CalendarX2 className="w-3.5 h-3.5" strokeWidth={2} />,
      collapsed: true,
    },
  ];

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header card */}
      <div className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5">
        <h1 className="text-[22px] sm:text-[24px] font-semibold tracking-tight text-slate-900">
          {t('adminTitle')}
        </h1>
        <p className="text-[13px] text-slate-500 mt-0.5">
          {t('adminDescription')}
        </p>
      </div>

      {/* Daily Status Dashboard */}
      <Card
        title={t('dailyStatus.title')}
        actions={
          <div className="flex items-center gap-1.5 flex-wrap">
            <button
              type="button"
              onClick={() => handleDateChange(-1)}
              aria-label={t('actions.previousMonth')}
              className="p-1.5 rounded-md bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors"
            >
              <ChevronLeft className="w-4 h-4" strokeWidth={2} />
            </button>
            <input
              type="date"
              value={dailyDate}
              onChange={(e) => handleDateInput(e.target.value)}
              className="px-2.5 py-1.5 rounded-md bg-white border border-slate-200 text-[13px] text-slate-900 tabular-nums focus:outline-none focus:ring-2 focus:ring-[#00A871]/30 focus:border-[#00A871]/50"
            />
            <button
              type="button"
              onClick={() => handleDateChange(1)}
              aria-label={t('actions.nextMonth')}
              className="p-1.5 rounded-md bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors"
            >
              <ChevronRight className="w-4 h-4" strokeWidth={2} />
            </button>
            {!isToday && (
              <Button variant="ghost" size="sm" onClick={goToToday}>
                {t('actions.today')}
              </Button>
            )}
            <span className="text-[12px] text-slate-500 tabular-nums ml-1 hidden sm:inline">
              {formatDateDisplay(dailyDate)}
            </span>
          </div>
        }
      >
        {dailyLoading ? (
          <div className="py-8"><Loading /></div>
        ) : dailyError ? (
          <EmptyState
            variant="error"
            title={t('errors.fetchDailyFailed')}
            onRetry={() => fetchDailyStatus(dailyDate)}
          />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4">
            {STATUS_DEFS.map((def) => (
              <StatusGroup
                key={def.key}
                label={def.label}
                tone={def.tone}
                icon={def.icon}
                employees={statusGroups[def.key]}
                onEdit={openEditFromDailyStatus}
                loadingId={dayLoadingForId}
                collapsed={def.collapsed}
                t={t}
              />
            ))}
          </div>
        )}
      </Card>

      {/* Monthly Employee View */}
      <Card
        title={t('monthView.title')}
        actions={
          <div className="flex items-center gap-2 flex-wrap w-full sm:w-auto">
            <select
              value={selectedEmployeeId ?? ''}
              onChange={(e) => setSelectedEmployee(e.target.value || null)}
              aria-label={t('monthView.selectEmployee')}
              className="flex-1 sm:flex-none sm:w-64 px-3 py-1.5 rounded-md bg-white border border-slate-200 text-[13px] text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#00A871]/30 focus:border-[#00A871]/50"
            >
              <option value="">{t('monthView.selectEmployee')}</option>
              {employees.map((emp) => (
                <option key={emp.id} value={emp.id}>
                  {emp.firstName} {emp.lastName}
                </option>
              ))}
            </select>
            {selectedEmployeeId && (
              <>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setIsAddDayOpen(true)}
                  leftIcon={<Plus className="w-4 h-4" strokeWidth={2} />}
                >
                  {t('monthView.addDay')}
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() =>
                    downloadReportFn(
                      'monthly',
                      { year: selectedMonth.year, month: selectedMonth.month },
                      selectedEmployeeId,
                    )
                  }
                  disabled={reportDownloading}
                  leftIcon={<FileDown className="w-4 h-4" strokeWidth={2} />}
                >
                  {tReports('actions.downloadMonthly')}
                </Button>
              </>
            )}
            <div className="inline-flex items-center gap-1">
              <button
                type="button"
                onClick={() => handleMonthChange(-1)}
                aria-label={t('actions.previousMonth')}
                className="p-1.5 rounded-md bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors"
              >
                <ChevronLeft className="w-4 h-4" strokeWidth={2} />
              </button>
              <span className="text-[13px] font-medium text-slate-900 tabular-nums min-w-[140px] text-center">
                {MONTH_NAMES_DE[selectedMonth.month - 1]} {selectedMonth.year}
              </span>
              <button
                type="button"
                onClick={() => handleMonthChange(1)}
                aria-label={t('actions.nextMonth')}
                className="p-1.5 rounded-md bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors"
              >
                <ChevronRight className="w-4 h-4" strokeWidth={2} />
              </button>
            </div>
          </div>
        }
      >
        {!selectedEmployeeId ? (
          <EmptyState
            icon={<Users className="w-12 h-12" strokeWidth={1.75} />}
            title={t('monthView.selectEmployee')}
            description={t('monthView.noData')}
          />
        ) : historyLoading ? (
          <div className="py-8"><Loading /></div>
        ) : historyError ? (
          <EmptyState
            variant="error"
            title={t('errors.fetchFailed')}
            onRetry={() => fetchEmployeeHistory(selectedEmployeeId, selectedMonth.year, selectedMonth.month)}
          />
        ) : employeeHistory.length === 0 ? (
          <EmptyState
            icon={<Clock className="w-12 h-12" strokeWidth={1.75} />}
            title={t('monthView.noData')}
          />
        ) : (
          <HistoryTable entries={employeeHistory} onEdit={openEdit} t={t} />
        )}
      </Card>

      {/* Überstundenkonto des gewählten Mitarbeiters */}
      {selectedEmployeeId && selectedBalance && (
        <>
          <OvertimeAccountCard balance={selectedBalance} />

          <Card
            title={t('overtime.monthlyTitle')}
            noPadding
            actions={
              <div className="flex flex-wrap gap-2">
                <Button variant="secondary" onClick={() => setIsSetupOpen(true)}>
                  {t('overtime.buttons.setUpAccount')}
                </Button>
              </div>
            }
          >
            <OvertimeMonthsTable months={selectedBalance.months} />
          </Card>

          {selectedBalance.adjustments.length > 0 && (
            <Card title={t('overtime.log.title')} noPadding>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeader>{t('overtime.log.date')}</TableHeader>
                    <TableHeader>{t('overtime.log.hours')}</TableHeader>
                    <TableHeader>{t('overtime.log.note')}</TableHeader>
                    <TableHeader className="hidden sm:table-cell">
                      {t('overtime.log.createdBy')}
                    </TableHeader>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedBalance.adjustments.map((entry) => (
                    <TableRow key={entry.overtimeAdjustmentId}>
                      <TableCell className="text-[13px] text-slate-700 tabular-nums">
                        {new Date(entry.effectiveDate).toLocaleDateString('de-DE')}
                      </TableCell>
                      <TableCell
                        className={`text-[13px] font-medium tabular-nums ${
                          entry.adjustmentHours < 0 ? 'text-red-600' : 'text-slate-900'
                        }`}
                      >
                        {formatSignedHours(entry.adjustmentHours)}
                      </TableCell>
                      <TableCell className="text-[13px] text-slate-700">{entry.note}</TableCell>
                      <TableCell className="hidden sm:table-cell text-[13px] text-slate-500">
                        {entry.createdBy ?? '–'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          )}


          <OvertimeSetupSlideOver
            isOpen={isSetupOpen}
            onClose={() => setIsSetupOpen(false)}
            employeeId={selectedEmployeeId}
            employeeName={selectedEmployeeName}
            currentStartDate={
              selectedBalance.hasConfiguredStartDate ? selectedBalance.periodStart : undefined
            }
          />
        </>
      )}

      {selectedEmployeeId && (
        <TimeClockAdminEditSlideOver
          isOpen={isAddDayOpen}
          onClose={() => setIsAddDayOpen(false)}
          entry={null}
          employeeId={selectedEmployeeId}
          employeeName={selectedEmployeeName}
          allowsHomeOffice={allowsHomeOffice(selectedEmployeeId)}
          pickDate
          existingDates={employeeHistory.map((d) => d.date ?? '').filter(Boolean)}
        />
      )}

      {/* Edit SlideOver */}
      <TimeClockAdminEditSlideOver
        isOpen={isEditOpen}
        onClose={closeEdit}
        entry={editEntry}
        employeeId={editEmployeeId}
        employeeName={editEmployeeName}
        allowsHomeOffice={allowsHomeOffice(editEmployeeId)}
      />
    </div>
  );
}

// --- Sub-components ---

type StatusTone = 'emerald' | 'amber' | 'red' | 'sky' | 'slate' | 'slate-soft';

const TONE_STYLES: Record<StatusTone, { wrapper: string; iconBg: string; iconText: string; chip: string }> = {
  emerald: {
    wrapper: 'border-emerald-200 bg-emerald-50/50',
    iconBg: 'bg-emerald-100',
    iconText: 'text-emerald-700',
    chip: 'bg-white text-emerald-700 border border-emerald-200',
  },
  amber: {
    wrapper: 'border-amber-200 bg-amber-50/50',
    iconBg: 'bg-amber-100',
    iconText: 'text-amber-700',
    chip: 'bg-white text-amber-700 border border-amber-200',
  },
  red: {
    wrapper: 'border-red-200 bg-red-50/50',
    iconBg: 'bg-red-100',
    iconText: 'text-red-700',
    chip: 'bg-white text-red-700 border border-red-200',
  },
  sky: {
    wrapper: 'border-sky-200 bg-sky-50/50',
    iconBg: 'bg-sky-100',
    iconText: 'text-sky-700',
    chip: 'bg-white text-sky-700 border border-sky-200',
  },
  slate: {
    wrapper: 'border-slate-200 bg-white',
    iconBg: 'bg-slate-100',
    iconText: 'text-slate-600',
    chip: 'bg-slate-100 text-slate-600 border border-slate-200',
  },
  'slate-soft': {
    wrapper: 'border-slate-200 bg-slate-50/60',
    iconBg: 'bg-slate-100',
    iconText: 'text-slate-500',
    chip: 'bg-slate-100 text-slate-500 border border-slate-200',
  },
};

interface StatusGroupProps {
  label: string;
  icon: React.ReactNode;
  tone: StatusTone;
  employees: FacilityFlowCoreModulesPlanningWorkflowsTimeClockDailyEmployeeStatusDto[];
  collapsed?: boolean;
  onEdit?: (emp: FacilityFlowCoreModulesPlanningWorkflowsTimeClockDailyEmployeeStatusDto) => void;
  loadingId?: string | null;
  t: (key: string) => string;
}

function StatusGroup({ label, icon, tone, employees, collapsed = false, onEdit, loadingId, t }: StatusGroupProps) {
  const [isCollapsed, setIsCollapsed] = useState(collapsed);
  const count = employees.length;
  const styles = TONE_STYLES[tone];

  return (
    <div className={`rounded-xl border ${styles.wrapper}`}>
      <button
        type="button"
        onClick={() => setIsCollapsed(!isCollapsed)}
        className="flex items-center justify-between w-full text-left px-3 py-2.5"
      >
        <div className="flex items-center gap-2 min-w-0">
          <span className={`inline-flex items-center justify-center w-6 h-6 rounded-md ${styles.iconBg} ${styles.iconText}`}>
            {icon}
          </span>
          <span className="text-[13px] font-semibold text-slate-900 truncate">{label}</span>
          <span className={`inline-flex items-center justify-center min-w-[1.25rem] px-1.5 h-5 rounded-full text-[11px] font-medium tabular-nums ${styles.chip}`}>
            {count}
          </span>
        </div>
        <ChevronRight
          className={`w-4 h-4 text-slate-400 flex-shrink-0 transition-transform ${isCollapsed ? '' : 'rotate-90'}`}
          strokeWidth={2}
        />
      </button>

      {!isCollapsed && (
        <div className="border-t border-slate-100 px-2 py-2 space-y-1">
          {count === 0 ? (
            <p className="text-[12px] text-slate-500 px-2 py-2">{t('dailyStatus.noEmployees')}</p>
          ) : (
            employees.map((emp) => (
              <EmployeeStatusRow
                key={emp.employeeId}
                employee={emp}
                onEdit={onEdit}
                isLoading={loadingId === emp.employeeId}
                t={t}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
}

function EmployeeStatusRow({
  employee: emp,
  onEdit,
  isLoading,
  t,
}: {
  employee: FacilityFlowCoreModulesPlanningWorkflowsTimeClockDailyEmployeeStatusDto;
  onEdit?: (emp: FacilityFlowCoreModulesPlanningWorkflowsTimeClockDailyEmployeeStatusDto) => void;
  isLoading?: boolean;
  t: (key: string) => string;
}) {
  const timeInfo = getEmployeeTimeInfo(emp, t);
  const initials = (emp.employeeName ?? '–')
    .split(' ')
    .map((p) => p.charAt(0))
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

  const content = (
    <>
      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-slate-100 text-[10px] font-semibold text-slate-600 flex-shrink-0">
        {initials || '–'}
      </span>
      <span className="text-[13px] font-medium text-slate-900 truncate flex-1">
        {emp.employeeName ?? '–'}
      </span>
      <span className="text-[11px] text-slate-500 tabular-nums truncate flex-shrink-0">
        {timeInfo}
      </span>
      {onEdit && (
        <span className="text-slate-300 group-hover:text-slate-600 transition-colors flex-shrink-0">
          {isLoading ? (
            <Clock className="w-3.5 h-3.5 animate-spin" strokeWidth={2} />
          ) : (
            <Pencil className="w-3.5 h-3.5" strokeWidth={2} />
          )}
        </span>
      )}
    </>
  );

  if (onEdit) {
    return (
      <button
        type="button"
        onClick={() => onEdit(emp)}
        disabled={isLoading}
        title={t('actions.edit')}
        className="group w-full flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-white hover:shadow-sm transition-all text-left disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {content}
      </button>
    );
  }
  return (
    <div className="w-full flex items-center gap-2 px-2 py-1.5">
      {content}
    </div>
  );
}

function getEmployeeTimeInfo(
  emp: FacilityFlowCoreModulesPlanningWorkflowsTimeClockDailyEmployeeStatusDto,
  t: (key: string) => string,
): string {
  const status = emp.status;
  if (status === AttendanceStatus.NUMBER_1 || status === AttendanceStatus.NUMBER_4) {
    // clockInTime is already the counted start (not before the work count start „Zählbeginn“, FACF-87/149).
    const since = emp.clockInTime ? formatUtcToLocalTime(emp.clockInTime) : null;
    const work = emp.totalWorkTime ? formatTimeSpan(emp.totalWorkTime) : null;
    const parts: string[] = [];
    if (since) parts.push(`${t('dailyStatus.since')} ${since}`);
    if (work) parts.push(work);
    return parts.join(' · ');
  }
  if (status === AttendanceStatus.NUMBER_2) {
    return emp.totalWorkTime ? formatTimeSpan(emp.totalWorkTime) : '';
  }
  if (status === AttendanceStatus.NUMBER_3) {
    return emp.plannedStartTime ? `${t('dailyStatus.planned')}: ${emp.plannedStartTime}` : '';
  }
  if (status === AttendanceStatus.NUMBER_5) {
    return emp.absenceTypeName ?? '';
  }
  return '';
}

function formatDate(date?: string) {
  if (!date) return '-';
  const [year, month, day] = date.split('-');
  if (!year || !month || !day) return date;
  return `${day}.${month}.${year}`;
}

interface HistoryTableProps {
  entries: FacilityFlowCoreModulesPlanningWorkflowsTimeClockTimeClockDayDto[];
  onEdit: (entry: FacilityFlowCoreModulesPlanningWorkflowsTimeClockTimeClockDayDto) => void;
  t: (key: string) => string;
}

function HistoryTable({ entries, onEdit, t }: HistoryTableProps) {
  const sorted = useMemo(
    () => [...entries].sort((a, b) => (a.date ?? '').localeCompare(b.date ?? '')),
    [entries],
  );

  return (
    <Table>
      <TableHead>
        <TableRow>
          <TableHeader>{t('fields.date')}</TableHeader>
          <TableHeader>{t('fields.events')}</TableHeader>
          <TableHeader className="hidden md:table-cell">{t('fields.workTime')}</TableHeader>
          <TableHeader className="hidden md:table-cell">{t('fields.breakTime')}</TableHeader>
          <TableHeader>{t('fields.status')}</TableHeader>
          <TableHeader>{t('actions.edit')}</TableHeader>
        </TableRow>
      </TableHead>
      <TableBody>
        {sorted.map((entry) => (
          <TableRow key={entry.timeClockDayId}>
            <TableCell>
              <div className="text-[13px] font-medium text-slate-900 tabular-nums">{formatDate(entry.date)}</div>
              <div className="sm:hidden mt-1">
                <EventBadges events={entry.events} compact t={t} />
              </div>
            </TableCell>
            <TableCell className="hidden sm:table-cell">
              <EventBadges events={entry.events} t={t} />
            </TableCell>
            <TableCell className="hidden md:table-cell tabular-nums">
              <WorkTimeWithCredit entry={entry} />
            </TableCell>
            <TableCell className="hidden md:table-cell tabular-nums">
              {formatTimeSpan(entry.totalBreakTime)}
            </TableCell>
            <TableCell>
              {entry.status === DayStatus.NUMBER_2 ? (
                <Badge variant="success">{t('monthView.confirmed')}</Badge>
              ) : (
                <Badge variant="warning">{t('monthView.open')}</Badge>
              )}
            </TableCell>
            <TableCell>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onEdit(entry)}
                title={t('actions.correct')}
                aria-label={t('actions.correct')}
                className="p-1.5"
              >
                <Pencil className="w-4 h-4" strokeWidth={2} />
              </Button>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

// Event badges (mirrors TimeClock user-view styling)

const eventBadgeConfig: Record<number, { shortKey: string; color: string; icon: string }> = {
  [EventType.NUMBER_1]: {
    shortKey: 'events.clockInShort',
    color: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
    icon: '→',
  },
  [EventType.NUMBER_2]: {
    shortKey: 'events.clockOutShort',
    color: 'bg-red-50 text-red-700 border border-red-200',
    icon: '←',
  },
  [EventType.NUMBER_3]: {
    shortKey: 'events.breakStartShort',
    color: 'bg-amber-50 text-amber-700 border border-amber-200',
    icon: '⏸',
  },
  [EventType.NUMBER_4]: {
    shortKey: 'events.breakEndShort',
    color: 'bg-sky-50 text-sky-700 border border-sky-200',
    icon: '▶',
  },
};

function EventBadges({
  events,
  compact = false,
  t,
}: {
  events?: FacilityFlowCoreModulesPlanningWorkflowsTimeClockTimeClockEventDto[] | null;
  compact?: boolean;
  t: (key: string) => string;
}) {
  const sortedEvents = sortEventsByTime(events);
  const workLocations = useDescribeWorkLocations()(sortedEvents);

  if (sortedEvents.length === 0) {
    return <span className="text-[12px] text-slate-400">–</span>;
  }

  return (
    <div className={`flex flex-wrap ${compact ? 'gap-1' : 'gap-1.5'}`}>
      {sortedEvents.map((evt, idx) => {
        const config = evt.eventType ? eventBadgeConfig[evt.eventType] : null;
        const workLocation = workLocations[idx];
        return (
          <span
            key={evt.timeClockEventId ?? idx}
            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[11px] font-medium tabular-nums ${config?.color ?? 'bg-slate-100 text-slate-600 border border-slate-200'}`}
          >
            <StampTime event={evt} />
            <span className="opacity-70">{compact ? (config?.icon ?? '•') : (config ? t(config.shortKey) : '?')}</span>
            {workLocation && <span className="font-normal opacity-80">· {workLocation}</span>}
          </span>
        );
      })}
    </div>
  );
}
